import { useMemo, useState } from 'react';
import { Target, AlertCircle } from 'lucide-react';
import useLocalState from '../hooks/useLocalState.js';
import useMarketData from '../hooks/useMarketData.js';
import { powerLawFairValue, powerLawBand, daysSinceGenesis, GENESIS } from '../lib/powerLaw.js';
import { formatUsd, formatBtc } from '../lib/format.js';

const PRESET_TARGETS = [
  { label: '0.1 BTC',       btc: 0.1 },
  { label: '0.21 BTC',      btc: 0.21 },
  { label: '0.5 BTC',       btc: 0.5 },
  { label: '1 BTC',         btc: 1 },
  { label: '2.1 BTC',       btc: 2.1 },
];

const FREQUENCY_DAYS = { weekly: 7, biweekly: 14, monthly: 30.44 };

function CardShell({ children }) {
  return (
    <div className="rounded-2xl bg-stone-900/60 border border-stone-800 p-6 hover:border-amber-500/40 transition-colors">
      {children}
    </div>
  );
}

export default function Goals() {
  // Read the user's stack (from My Stack) and DCA config (from DCA sim)
  // directly from localStorage so we have a single source of truth.
  const [stack] = useLocalState('myStack', { btc: 0 }, 1);
  const [dcaConfig] = useLocalState('dcaConfig', { amount: 0, frequency: 'monthly' }, 1);
  const [goal, setGoal] = useLocalState('goal', { type: 'btc', btc: 1 }, 1);

  const [customInput, setCustomInput] = useState('');
  const [showCustom, setShowCustom] = useState(false);

  const { btc: marketBtc } = useMarketData();
  const spot = marketBtc.spot;

  // Resolve the target to a BTC amount
  const targetBtc = useMemo(() => {
    if (goal.type === 'btc') return goal.btc;
    if (goal.type === 'usd' && Number.isFinite(spot) && spot > 0) {
      return goal.usd / spot;
    }
    if (goal.type === 'fairValue') {
      const fair = powerLawFairValue();
      if (Number.isFinite(spot) && spot > 0) return goal.usd / spot;
      return null;
    }
    return null;
  }, [goal, spot]);

  const remaining = useMemo(() => {
    if (!Number.isFinite(targetBtc) || !Number.isFinite(stack.btc)) return null;
    return Math.max(0, targetBtc - stack.btc);
  }, [targetBtc, stack.btc]);

  const progressPct = useMemo(() => {
    if (!Number.isFinite(targetBtc) || targetBtc <= 0 || !Number.isFinite(stack.btc)) return 0;
    return Math.min(100, (stack.btc / targetBtc) * 100);
  }, [targetBtc, stack.btc]);

  // Estimate days to reach goal using the DCA config's saving rate.
  // This uses today's spot price for "how many BTC per contribution" —
  // it's a snapshot estimate, not a power-law projection. The DCA
  // simulator already does the forward-model projection; this is the
  // simpler "at today's price, how long" read.
  const estimate = useMemo(() => {
    if (!Number.isFinite(remaining) || remaining <= 0) {
      return { reached: true };
    }
    const amount = dcaConfig.amount;
    const freq = dcaConfig.frequency;
    const stepDays = FREQUENCY_DAYS[freq];
    if (!Number.isFinite(amount) || amount <= 0 || !stepDays) {
      return { noRate: true };
    }
    if (!Number.isFinite(spot) || spot <= 0) {
      return { noPrice: true };
    }
    const btcPerPeriod = amount / spot;
    if (btcPerPeriod <= 0) return { noRate: true };
    const periodsNeeded = remaining / btcPerPeriod;
    const daysNeeded = periodsNeeded * stepDays;
    const targetDate = new Date(Date.now() + daysNeeded * 86_400_000);
    return {
      daysNeeded: Math.ceil(daysNeeded),
      targetDate,
      periodsNeeded: Math.ceil(periodsNeeded),
      btcPerPeriod,
    };
  }, [remaining, dcaConfig, spot]);

  function selectPreset(preset) {
    setGoal({ type: 'btc', btc: preset.btc });
    setShowCustom(false);
  }

  function commitCustom(e) {
    e?.preventDefault();
    const val = parseFloat(customInput);
    if (!Number.isFinite(val) || val <= 0) return;
    setGoal({ type: 'btc', btc: val });
    setShowCustom(false);
  }

  return (
    <CardShell>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-500 to-amber-400 flex items-center justify-center">
          <Target size={20} className="text-stone-900" />
        </div>
        <h3 className="font-display text-lg font-semibold text-cream">Goal</h3>
      </div>

      {/* Target selection */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wider text-stone-500 mb-2">Target</div>
        <div className="flex flex-wrap gap-1.5">
          {PRESET_TARGETS.map(p => (
            <button
              key={p.label}
              onClick={() => selectPreset(p)}
              className={`px-3 py-1.5 rounded-lg text-xs font-mono transition ${
                goal.type === 'btc' && goal.btc === p.btc
                  ? 'bg-amber-400/20 text-amber-400 border border-amber-400/40'
                  : 'bg-stone-800/60 text-stone-400 border border-stone-700 hover:border-stone-600'
              }`}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={() => setShowCustom(true)}
            className={`px-3 py-1.5 rounded-lg text-xs font-mono transition ${
              showCustom
                ? 'bg-amber-400/20 text-amber-400 border border-amber-400/40'
                : 'bg-stone-800/60 text-stone-400 border border-stone-700 hover:border-stone-600'
            }`}
          >
            Custom
          </button>
        </div>
        {showCustom && (
          <form onSubmit={commitCustom} className="flex gap-2 mt-2">
            <input
              type="number"
              inputMode="decimal"
              step="0.00000001"
              min="0"
              value={customInput}
              onChange={e => setCustomInput(e.target.value)}
              autoFocus
              placeholder="e.g. 0.5"
              className="flex-1 bg-stone-950/60 border border-stone-700 rounded-lg px-3 py-1.5 text-cream font-mono text-sm focus:outline-none focus:border-amber-500/60"
            />
            <span className="self-center text-xs text-stone-500 font-mono">BTC</span>
            <button
              type="submit"
              className="px-3 py-1.5 rounded-lg bg-amber-400/20 text-amber-400 border border-amber-400/40 text-xs font-semibold"
            >
              Set
            </button>
          </form>
        )}
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex items-baseline justify-between mb-1.5">
          <span className="text-xs text-stone-500">
            {formatBtc(stack.btc)} of {formatBtc(targetBtc ?? 0)}
          </span>
          <span className="text-xs font-mono text-stone-400 tabular-nums">
            {progressPct.toFixed(1)}%
          </span>
        </div>
        <div className="h-3 w-full rounded-full bg-stone-800 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-orange-500 to-amber-400 transition-all duration-500"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      {/* Timeline estimate */}
      <div className="rounded-lg bg-stone-950/40 p-3">
        {estimate.reached ? (
          <div className="text-center py-2">
            <div className="text-2xl mb-1">🎉</div>
            <div className="text-sm font-semibold text-amber-400">Goal reached!</div>
            <div className="text-[11px] text-stone-500 mt-1">Your stack already meets or exceeds this target.</div>
          </div>
        ) : estimate.noRate ? (
          <div className="flex items-start gap-2 text-xs text-stone-500">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-stone-500" />
            <span>
              Set your saving rate in the DCA Simulator (amount + frequency) to see
              a timeline estimate here.
            </span>
          </div>
        ) : estimate.noPrice ? (
          <div className="flex items-start gap-2 text-xs text-stone-500">
            <AlertCircle size={14} className="flex-shrink-0 mt-0.5 text-stone-500" />
            <span>Waiting for BTC price to compute timeline...</span>
          </div>
        ) : (
          <>
            <div className="text-[10px] uppercase tracking-wider text-stone-500 mb-1">
              At today's price + your current saving rate
            </div>
            <div className="flex items-baseline gap-2 mb-2">
              <span className="font-mono tabular-nums text-xl font-bold text-cream">
                {estimate.daysNeeded > 365
                  ? `${(estimate.daysNeeded / 365.25).toFixed(1)} years`
                  : `${estimate.daysNeeded} days`}
              </span>
              <span className="text-xs text-stone-500">to reach target</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-xs text-stone-500">
              <div>
                <span className="text-stone-600">Target date: </span>
                <span className="text-stone-400 font-mono tabular-nums">
                  {estimate.targetDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                </span>
              </div>
              <div>
                <span className="text-stone-600">Remaining: </span>
                <span className="text-stone-400 font-mono tabular-nums">
                  {formatBtc(remaining)}
                </span>
              </div>
              <div>
                <span className="text-stone-600">Saving: </span>
                <span className="text-stone-400 font-mono tabular-nums">
                  {formatUsd(dcaConfig.amount)}/{dcaConfig.frequency === 'biweekly' ? '2wk' : dcaConfig.frequency === 'weekly' ? 'wk' : 'mo'}
                </span>
              </div>
              <div>
                <span className="text-stone-600">≈ per buy: </span>
                <span className="text-stone-400 font-mono tabular-nums">
                  {estimate.btcPerPeriod < 0.001
                    ? `${Math.round(estimate.btcPerPeriod * 1e8).toLocaleString()} sats`
                    : formatBtc(estimate.btcPerPeriod)}
                </span>
              </div>
            </div>
            <p className="text-[10px] text-stone-600 mt-2">
              Snapshot estimate at today's spot ({formatUsd(spot)}). Real timeline will
              shift as price moves — the DCA Simulator shows the full power-law range.
            </p>
          </>
        )}
      </div>
    </CardShell>
  );
}
